import cx from "classnames";
import { useEffect, useRef, useState } from "react";

import { useTranslation } from "@/core/i18n";
import { Models } from "api/types";
import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import ChevronRight from "atoms/icon/icons/chevron_right.svg";

export interface TreatmentCardProps {
  id: string;
  imgUrl?: string | null;
  type: string;
  title: string;
  remainingTreatmentsCount?: number;
  packageUnitType?: Models<"PackageUnitTypeModel"> | null | undefined;
  onClick?: () => void;
  onBookAppointment?: (e: React.MouseEvent) => void;
  onRedeem?: (quantity: number, selections?: Record<string, number>) => void;
  membershipName?: string | null;
  isMemberBenefit?: boolean;
  redeemButtonText?: string;
  showQuantitySelector?: boolean;
  maxQuantity?: number;
  onCancelQuantitySelector?: () => void;
  isRedeeming?: boolean;
  redeemedToday?: number;
  onConfettiTrigger?: (element: HTMLElement) => void;
  showTreatmentSelector?: boolean;
  showRemainingQuantity?: boolean;
  treatments?: Array<{
    id: string;
    name: string;
    remainingQuantity: number;
  }>;
  onTreatmentSelect?: (selections: Record<string, number>) => void;
}

// Helper function to clean up membership name by removing trailing "Member" or "Membership"
const cleanMembershipName = (name: string): string => {
  return name
    .replace(/\s+Members?$/i, "")
    .replace(/\s+Memberships?$/i, "")
    .trim();
};

export const TreatmentCard = ({
  imgUrl,
  title,
  remainingTreatmentsCount,
  onClick,
  onBookAppointment,
  onRedeem,
  packageUnitType,
  type,
  membershipName,
  isMemberBenefit,
  redeemButtonText,
  showQuantitySelector,
  maxQuantity = 1,
  onCancelQuantitySelector,
  isRedeeming,
  redeemedToday = 0,
  onConfettiTrigger,
  showTreatmentSelector,
  showRemainingQuantity,
  treatments = [],
  onTreatmentSelect,
}: TreatmentCardProps) => {
  const { t } = useTranslation();
  const cleanedMembershipName = membershipName ? cleanMembershipName(membershipName) : null;

  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [treatmentQuantities, setTreatmentQuantities] = useState<Record<string, number>>({});
  const cardRef = useRef<HTMLDivElement>(null);
  const prevRedeemedToday = useRef(redeemedToday);

  // Trigger confetti when redeemedToday increases
  useEffect(() => {
    if (redeemedToday > prevRedeemedToday.current && cardRef.current && onConfettiTrigger) {
      onConfettiTrigger(cardRef.current);
    }
    prevRedeemedToday.current = redeemedToday;
  }, [redeemedToday, onConfettiTrigger]);

  const handleRedeemWithQuantity = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRedeem) {
      onRedeem(selectedQuantity);
    }
  };

  const handleRedeemClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRedeem) {
      onRedeem(1); // Default to 1 for initial click
    }
  };

  return (
    <div
      ref={cardRef}
      className="group relative overflow-hidden rounded-lg bg-white shadow-sm transition-shadow active:scale-[0.98]"
      onClick={showQuantitySelector ? undefined : onClick}
      style={{ cursor: showQuantitySelector ? "default" : "pointer" }}
    >
      {/* White content section */}
      <div className="bg-white px-4 pb-5 pt-5">
        {showTreatmentSelector ? (
          /* Treatment Selector View with inline quantity selection */
          <>
            <h3 className="mb-3 font-serif text-bold1 text-primary">
              {t("treatmentCard.whichOptionsToRedeem")}
            </h3>

            {/* Treatment options with quantity selectors */}
            <div className="mb-4 flex flex-col gap-3">
              {treatments.map((treatment) => {
                const quantity = treatmentQuantities[treatment.id] || 0;
                return (
                  <div
                    key={treatment.id}
                    className={cx(
                      "rounded-lg border-2 p-3 transition-all",
                      quantity > 0
                        ? "border-brand-color bg-brand-color/10"
                        : "border-border-one bg-white",
                    )}
                  >
                    <div className="mb-2 flex items-start justify-between">
                      <div>
                        <div className="text-bold2 text-primary">{treatment.name}</div>
                        <div className="text-body3 text-secondary">
                          {t("treatmentCard.remainingAvailable", {
                            count: treatment.remainingQuantity,
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Inline quantity selector */}
                    <div className="flex items-center gap-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setTreatmentQuantities((prev) => ({
                            ...prev,
                            [treatment.id]: Math.max(0, (prev[treatment.id] || 0) - 1),
                          }));
                        }}
                        disabled={quantity <= 0}
                        className={cx(
                          "flex h-8 w-8 items-center justify-center rounded-full border-2 text-bold1 transition-all",
                          quantity <= 0
                            ? "border-border-one bg-background-one cursor-not-allowed text-dark-grey"
                            : "border-brand-color bg-white text-brand-color hover:bg-brand-color hover:text-white",
                        )}
                      >
                        −
                      </button>

                      <input
                        type="number"
                        min="0"
                        max={treatment.remainingQuantity}
                        value={quantity}
                        onChange={(e) => {
                          e.stopPropagation();
                          const val = parseInt(e.target.value) || 0;
                          const clamped = Math.max(0, Math.min(treatment.remainingQuantity, val));
                          setTreatmentQuantities((prev) => ({
                            ...prev,
                            [treatment.id]: clamped,
                          }));
                        }}
                        className="border-border-one w-16 rounded-lg border-2 px-2 py-1 text-center text-bold1 text-primary"
                      />

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setTreatmentQuantities((prev) => ({
                            ...prev,
                            [treatment.id]: Math.min(
                              treatment.remainingQuantity,
                              (prev[treatment.id] || 0) + 1,
                            ),
                          }));
                        }}
                        disabled={quantity >= treatment.remainingQuantity}
                        className={cx(
                          "flex h-8 w-8 items-center justify-center rounded-full border-2 text-bold1 transition-all",
                          quantity >= treatment.remainingQuantity
                            ? "border-border-one bg-background-one cursor-not-allowed text-dark-grey"
                            : "border-brand-color bg-white text-brand-color hover:bg-brand-color hover:text-white",
                        )}
                      >
                        +
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Action buttons */}
            <div className="flex gap-2">
              <Button
                size="xsmall"
                style="secondary"
                fullWidth
                onClick={(e) => {
                  e.stopPropagation();
                  setTreatmentQuantities({});
                  onCancelQuantitySelector?.();
                }}
              >
                {t("treatmentCard.cancel")}
              </Button>
              <Button
                size="xsmall"
                style="primary"
                fullWidth
                disabled={Object.values(treatmentQuantities).every((q) => q === 0)}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onTreatmentSelect) {
                    // Filter out 0 quantities
                    const selections = Object.fromEntries(
                      Object.entries(treatmentQuantities).filter(([_, qty]) => qty > 0),
                    );
                    onTreatmentSelect(selections);
                  }
                }}
              >
                {t("treatmentCard.redeem")}
              </Button>
            </div>
          </>
        ) : showQuantitySelector ? (
          /* Quantity Selector View */
          <>
            <h3 className="mb-3 font-serif text-bold1 text-primary">
              {t("treatmentCard.howManyToRedeem")}
            </h3>

            {/* Quantity stepper */}
            <div className="mb-4 flex items-center justify-center gap-4">
              <button
                onClick={() => setSelectedQuantity(Math.max(1, selectedQuantity - 1))}
                disabled={selectedQuantity <= 1}
                className={cx(
                  "flex h-10 w-10 items-center justify-center rounded-full border-2 text-bold1 transition-all",
                  selectedQuantity <= 1
                    ? "border-border-one bg-background-one cursor-not-allowed text-dark-grey"
                    : "border-brand-color bg-white text-brand-color hover:bg-brand-color hover:text-white",
                )}
              >
                −
              </button>
              <div className="flex min-w-[80px] items-center justify-center">
                <input
                  type="number"
                  min="1"
                  max={maxQuantity}
                  value={selectedQuantity}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 1;
                    setSelectedQuantity(Math.max(1, Math.min(maxQuantity, val)));
                  }}
                  className="border-border-one w-full rounded-lg border-2 px-3 py-2 text-center text-h3 font-bold text-primary focus:border-brand-color focus:outline-none"
                />
              </div>
              <button
                onClick={() => setSelectedQuantity(Math.min(maxQuantity, selectedQuantity + 1))}
                disabled={selectedQuantity >= maxQuantity}
                className={cx(
                  "flex h-10 w-10 items-center justify-center rounded-full border-2 text-bold1 transition-all",
                  selectedQuantity >= maxQuantity
                    ? "border-border-one bg-background-one cursor-not-allowed text-dark-grey"
                    : "border-brand-color bg-white text-brand-color hover:bg-brand-color hover:text-white",
                )}
              >
                +
              </button>
            </div>

            <div className="mb-3 text-center text-body2 text-secondary">
              {t("treatmentCard.remainingAvailable", { count: maxQuantity })}
            </div>

            {/* Action buttons */}
            <div className="flex gap-2">
              <Button
                size="xsmall"
                style="secondary"
                fullWidth
                onClick={(e) => {
                  e.stopPropagation();
                  onCancelQuantitySelector?.();
                }}
              >
                {t("treatmentCard.cancel")}
              </Button>
              <Button
                size="xsmall"
                style="primary"
                fullWidth
                onClick={handleRedeemWithQuantity}
                disabled={isRedeeming}
              >
                {isRedeeming
                  ? t("treatmentCard.redeeming")
                  : t("treatmentCard.redeemQuantity", { quantity: selectedQuantity })}
              </Button>
            </div>
          </>
        ) : (
          /* Normal View */
          <>
            {/* Membership badge - cleaner styling */}
            {isMemberBenefit && cleanedMembershipName && (
              <div className="mb-1">
                <span className="text-body3 italic text-secondary">
                  {t("treatmentCard.fromYourMembership", {
                    membershipName: cleanedMembershipName,
                  })}
                </span>
              </div>
            )}

            {/* Treatment info with chevron */}
            <div className="mb-0.5 flex items-center justify-between">
              <h3 className="font-serif text-bold1 text-primary">{title}</h3>
              <Icon
                svg={ChevronRight}
                size="xsmall"
                color="darkGrey"
                className="ml-2 flex-shrink-0"
              />
            </div>
            {showRemainingQuantity && (
              <div className="mb-2">
                <p className="text-body2 text-secondary">
                  {remainingTreatmentsCount}{" "}
                  {type === "Package"
                    ? t("treatmentCard.nounTreatment", { count: remainingTreatmentsCount ?? 0 })
                    : (((remainingTreatmentsCount ?? 0) === 1
                        ? packageUnitType?.singularUnitType
                        : packageUnitType?.pluralUnitType) ??
                      t("treatmentCard.nounUnit", { count: remainingTreatmentsCount ?? 0 }))}{" "}
                  {t("treatmentCard.remaining")}
                  {redeemedToday > 0 && (
                    <span>
                      {" "}
                      ({t("treatmentCard.redeemedTodayCount", { count: redeemedToday })})
                    </span>
                  )}
                </p>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-2">
              {onRedeem && (
                <Button
                  size="xsmall"
                  style="primary"
                  fullWidth
                  onClick={handleRedeemClick}
                  disabled={isRedeeming}
                >
                  {isRedeeming
                    ? t("treatmentCard.redeeming")
                    : redeemButtonText || t("treatmentCard.redeem")}
                </Button>
              )}
              {onBookAppointment && (
                <Button size="xsmall" style="secondary" fullWidth onClick={onBookAppointment}>
                  {t("treatmentCard.book")}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
